import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useCan } from "@/auth/permissions";
import { PageShell, LoadingState, ErrorState } from "@/shared/ui/PageHeader";
import { createPlace, deletePlace, errMessage, listPlaces, MEDIA_QK, patchPlace, type Place } from "@/services/mediaLibrary";
import { SettingsTabs } from "./SettingsTabs";

const inp = "rounded-md border border-slate-300 px-2.5 py-1.5 text-[13px] focus:border-blue-500 focus:outline-none";
const btn = "rounded-md border border-slate-300 bg-white px-2.5 py-1 text-[12px] font-medium text-slate-600 hover:bg-slate-50";

/**
 * IMGLIB-1 · ⑤ 系统设置 → 地点清单。契约：designs/mockups/image-library/图片库-样稿.html 第 5 页。
 * 图片库的「地点」、景点的「所在地点」都从这里选，不手打。改名会同步到所有图片名字（如「西雅图-太空针塔-01」，名字现拼）；
 * 有图片或景点在用的地点不能删，只能停用。接口：GET / POST / PATCH / DELETE /api/v1/settings/places（改 = media.library.manage）。
 */
export function PlacesSettingsPage() {
  const qc = useQueryClient();
  const canManage = useCan("media.library.manage");
  const q = useQuery({ queryKey: MEDIA_QK.places, queryFn: listPlaces });
  const [adding, setAdding] = useState(false);
  const [nameCn, setNameCn] = useState("");
  const [nameEn, setNameEn] = useState("");
  const [editing, setEditing] = useState<{ id: string; name_cn: string; name_en: string } | null>(null);
  const [err, setErr] = useState<string | null>(null);

  const done = () => { setErr(null); qc.invalidateQueries({ queryKey: MEDIA_QK.places }); qc.invalidateQueries({ queryKey: MEDIA_QK.libraryAll }); };
  const fail = (e: unknown) => setErr(errMessage(e, "操作失败，请重试"));
  const addMut = useMutation({ mutationFn: () => createPlace({ name_cn: nameCn.trim(), name_en: nameEn.trim() || null }), onSuccess: () => { setAdding(false); setNameCn(""); setNameEn(""); done(); }, onError: fail });
  const patchMut = useMutation({ mutationFn: (v: { id: string; body: Parameters<typeof patchPlace>[1] }) => patchPlace(v.id, v.body), onSuccess: () => { setEditing(null); done(); }, onError: fail });
  const delMut = useMutation({ mutationFn: (id: string) => deletePlace(id), onSuccess: done, onError: fail });

  if (q.isError) return <ErrorState message="地点清单加载失败" onRetry={() => q.refetch()} />;
  if (!q.data) return <LoadingState />;
  const rows: Place[] = q.data;

  return (
    <PageShell title="系统设置" subtitle="地点清单 · 图片库的「地点」和景点的「所在地点」都从这里选">
      <SettingsTabs />
      <section className="flex flex-col gap-3 rounded-xl border border-slate-200 bg-white p-5" data-testid="places-card">
        <div className="flex items-center justify-between">
          <h2 className="text-[14px] font-bold text-slate-900">地点清单</h2>
          {canManage && !adding && <button type="button" data-testid="place-add" onClick={() => setAdding(true)} className="rounded-md bg-blue-600 px-3 py-1.5 text-[12.5px] font-medium text-white hover:bg-blue-700">＋ 新增地点</button>}
        </div>
        <p className="text-[12.5px] text-slate-500">图片库的「地点」、景点的「所在地点」都从这里选，不手打。改名会同步到所有图片名字（如「西雅图-太空针塔-01」）；有图片或景点在用的地点不能删，只能停用。</p>
        {adding && (
          <div className="flex flex-wrap items-center gap-2 rounded-lg border border-blue-200 bg-blue-50/40 p-3">
            <input aria-label="中文名" className={inp} placeholder="中文名，如 西雅图" value={nameCn} onChange={(e) => setNameCn(e.target.value)} />
            <input aria-label="英文名" className={inp} placeholder="英文名（选填）" value={nameEn} onChange={(e) => setNameEn(e.target.value)} />
            <button type="button" data-testid="place-add-save" disabled={!nameCn.trim() || addMut.isPending} onClick={() => addMut.mutate()} className="rounded-md bg-blue-600 px-3 py-1.5 text-[12.5px] font-medium text-white disabled:opacity-50">保存</button>
            <button type="button" onClick={() => setAdding(false)} className={btn}>取消</button>
          </div>
        )}
        {err && <p className="text-[12.5px] text-rose-600" role="alert">{err}</p>}
        <table className="w-full text-[13px]">
          <thead><tr className="border-b border-slate-200 text-left text-[12px] text-slate-500"><th className="py-2">地点</th><th>英文</th><th>景点</th><th>图片</th><th>状态</th><th className="text-right">操作</th></tr></thead>
          <tbody>
            {rows.length === 0 && <tr><td colSpan={6} className="py-8 text-center text-slate-400">还没有地点，先新增几个（如 西雅图、黄石、拉斯维加斯）</td></tr>}
            {rows.map((p) => {
              const e = editing?.id === p.id ? editing : null;
              const unused = p.attraction_count === 0 && p.image_count === 0;
              return (
                <tr key={p.id} className="border-b border-slate-100" data-testid="place-row">
                  <td className="py-2.5">{e ? <input aria-label="地点中文名" className={inp} value={e.name_cn} onChange={(ev) => setEditing({ ...e, name_cn: ev.target.value })} /> : <b>{p.name_cn}</b>}</td>
                  <td>{e ? <input aria-label="地点英文名" className={inp} value={e.name_en} onChange={(ev) => setEditing({ ...e, name_en: ev.target.value })} /> : (p.name_en ?? "—")}</td>
                  <td className="tabular-nums">{p.attraction_count}</td>
                  <td className="tabular-nums">{p.image_count}</td>
                  <td>{p.status === "active"
                    ? <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-50 px-2.5 py-0.5 text-[12px] font-medium text-emerald-600"><span className="h-[7px] w-[7px] rounded-full bg-current" />启用</span>
                    : <span className="inline-flex items-center gap-1.5 rounded-full bg-slate-100 px-2.5 py-0.5 text-[12px] font-medium text-slate-500"><span className="h-[7px] w-[7px] rounded-full bg-current" />停用</span>}</td>
                  <td className="space-x-1.5 text-right">
                    {canManage && (e ? (
                      <>
                        <button type="button" data-testid="place-rename-save" disabled={!e.name_cn.trim() || patchMut.isPending} onClick={() => patchMut.mutate({ id: p.id, body: { name_cn: e.name_cn.trim(), name_en: e.name_en.trim() || null } })} className="rounded-md bg-blue-600 px-2.5 py-1 text-[12px] font-medium text-white disabled:opacity-50">保存</button>
                        <button type="button" onClick={() => setEditing(null)} className={btn}>取消</button>
                      </>
                    ) : (
                      <>
                        <button type="button" data-testid="place-rename" onClick={() => { setErr(null); setEditing({ id: p.id, name_cn: p.name_cn, name_en: p.name_en ?? "" }); }} className={btn}>改名</button>
                        <button type="button" data-testid="place-toggle" onClick={() => patchMut.mutate({ id: p.id, body: { status: p.status === "active" ? "disabled" : "active" } })} className={btn}>{p.status === "active" ? "停用" : "启用"}</button>
                        {unused && <button type="button" data-testid="place-delete" onClick={() => { if (window.confirm(`删除地点「${p.name_cn}」？`)) delMut.mutate(p.id); }} className={`${btn} text-rose-600`}>删除</button>}
                      </>
                    ))}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        <p className="rounded-lg bg-slate-50 px-3 py-2 text-[12px] text-slate-500">
          景点那边：门票管理 → 景点详情有必选项「所在地点」（从这份清单选）。上传图片选了景点，地点自动带出；没有景点的图（城市风光 / 车辆 / 团队）选地点 + 固定类别。存量景点「所在地点」为空的，在景点列表里标「待补地点」，由同事补，系统不猜。
        </p>
      </section>
    </PageShell>
  );
}
